/* Мелкие UI-элементы макета: статус, госномер, пустое состояние, всплывающее меню, подсказка графиков. */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { OFFER_STATUS, STATUS, type OfferDisplayStatus, type OrderStatus, type Vehicle } from "@okno/shared";
import { Icon } from "./Icon";

export function StatusPill({ status }: { status: OrderStatus }) {
  const m = STATUS[status] || { label: status, tone: "muted" };
  return <span className={"status tone-" + m.tone}>{m.label}</span>;
}

export function OfferStatusPill({ status }: { status: OfferDisplayStatus }) {
  const m = OFFER_STATUS[status];
  return <span className={"status tone-" + m.tone}>{m.label}</span>;
}

/** Казахстанский госномер: «123 ABC 02» → флаг KZ, номер, регион. */
export function Plate({ plate, lg }: { plate: string; lg?: boolean }) {
  const parts = String(plate).split(" ");
  const region = parts.length > 1 ? parts.pop() : "";
  return (
    <span className={"plate" + (lg ? " plate-lg" : "")} aria-label={"Госномер " + plate}>
      <span className="flag">KZ</span>
      <span className="code">{parts.join(" ")}</span>
      {region ? <span className="region">{region}</span> : null}
    </span>
  );
}

export function carLine(v: Vehicle | null | undefined): string {
  if (!v) return "";
  return (v.make + " " + v.model).trim() + (v.color ? ", " + v.color.toLowerCase() : "");
}

export function Empty({ icon, title, text, action }: { icon: string; title: ReactNode; text?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <Icon name={icon} size={28} />
      <strong>{title}</strong>
      {text ? <span>{text}</span> : null}
      {action}
    </div>
  );
}

export function Spinner({ text = "Загрузка…" }: { text?: string }) {
  return (
    <div className="empty" aria-busy="true">
      <span className="muted">{text}</span>
    </div>
  );
}

export interface MenuItem {
  label: string;
  icon?: string;
  fn: () => void;
}

/** Кнопка с выпадающим меню (позиционируется у кнопки, закрывается кликом вне). */
export function MenuButton({ items, children, className, ariaLabel }: { items: MenuItem[]; children: ReactNode; className?: string; ariaLabel?: string }) {
  const [pos, setPos] = useState<{ top: number; left: number; up: boolean } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!pos) return;
    const close = (e: PointerEvent) => {
      if (menu.current?.contains(e.target as Node) || btn.current?.contains(e.target as Node)) return;
      setPos(null);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setPos(null);
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", esc);
    menu.current?.querySelector("button")?.focus();
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [pos]);
  const open = () => {
    const r = btn.current!.getBoundingClientRect();
    const left = Math.max(8, Math.min(window.innerWidth - 248, r.right - 240));
    // у нижнего края (нижняя навигация на телефоне) открываем вверх
    const up = r.bottom + 6 + items.length * 38 + 12 > window.innerHeight && r.top > window.innerHeight / 2;
    setPos({ top: window.scrollY + (up ? r.top - 6 : r.bottom + 6), left, up });
  };
  return (
    <>
      <button ref={btn} className={className ?? "btn btn-icon"} aria-label={ariaLabel} aria-haspopup="menu" aria-expanded={!!pos} onClick={() => (pos ? setPos(null) : open())}>
        {children}
      </button>
      {pos
        ? createPortal(
            <div ref={menu} className="menu" role="menu" style={{ top: pos.top, left: pos.left, transform: pos.up ? "translateY(-100%)" : undefined }}>
              {items.map((it) => (
                <button
                  key={it.label}
                  role="menuitem"
                  onClick={() => {
                    setPos(null);
                    it.fn();
                  }}
                >
                  {it.icon ? <Icon name={it.icon} size={16} /> : null}
                  {it.label}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/* ---------- подсказки графиков ---------- */

let tipEl: HTMLDivElement | null = null;

/** Показ подсказки у курсора. Текст вставляется через textContent — без HTML. Первая строка — жирная. */
function showTip(x: number, y: number, text: string): void {
  if (!tipEl) {
    tipEl = document.createElement("div");
    tipEl.className = "tip";
    document.body.appendChild(tipEl);
  }
  const [head, ...rest] = text.split("\n");
  const strong = document.createElement("strong");
  strong.textContent = head;
  tipEl.replaceChildren(strong, ...rest.map((line) => Object.assign(document.createElement("div"), { textContent: line })));
  tipEl.style.display = "block";
  const r = tipEl.getBoundingClientRect();
  let left = x + 14;
  let top = y - r.height - 10;
  if (left + r.width > window.innerWidth - 8) left = x - r.width - 14;
  if (top < 8) top = y + 16;
  tipEl.style.left = left + "px";
  tipEl.style.top = top + "px";
}

export function hideTip(): void {
  if (tipEl) tipEl.style.display = "none";
}

/** Пропсы для элемента с подсказкой: наведение и фокус с клавиатуры. */
export function tipProps(text: string) {
  return {
    tabIndex: 0,
    onPointerMove: (e: React.PointerEvent) => showTip(e.clientX, e.clientY, text),
    onPointerLeave: hideTip,
    onFocus: (e: React.FocusEvent) => {
      const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
      showTip(r.left + r.width / 2, r.top, text);
    },
    onBlur: hideTip,
  };
}
